import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, ChevronDown, Info, User as UserIcon, Wrench } from 'lucide-react';
import type { ChatMessageToolCall, ChatStoredMessage } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { JsonView } from '@/components/data/JsonView';
import { cn, formatDate } from '@/lib/utils';

interface Props {
  message: ChatStoredMessage;
  footer?: React.ReactNode;
}

export function MessageBubble({ message, footer }: Props): JSX.Element {
  const { t } = useTranslation();
  if (message.role === 'system') {
    return (
      <div className="flex justify-center">
        <div className="flex max-w-[80%] items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs italic text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{message.content}</span>
        </div>
      </div>
    );
  }

  const isUser = message.role === 'user';
  return (
    <div className={cn('flex gap-2', isUser ? 'flex-row-reverse' : 'flex-row')}>
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted',
        )}
      >
        {isUser ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      <div className={cn('flex max-w-[85%] flex-col gap-1.5', isUser ? 'items-end' : 'items-start')}>
        <div
          className={cn(
            'rounded-lg px-3 py-2 text-sm leading-relaxed',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'border bg-card text-card-foreground',
          )}
        >
          {message.content ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : !isUser ? (
            <p className="italic text-muted-foreground">{t('chat.messages.onlyTools')}</p>
          ) : null}
          {footer ? <div className="mt-2">{footer}</div> : null}
        </div>
        {!isUser && message.toolCalls && message.toolCalls.length > 0 ? (
          <ToolCallsAccordion calls={message.toolCalls} />
        ) : null}
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <span>{formatDate(message.createdAt)}</span>
          {message.tokensIn != null || message.tokensOut != null ? (
            <span>
              · {t('chat.messages.in')}={message.tokensIn ?? 0} {t('chat.messages.out')}={message.tokensOut ?? 0}
              {message.tokensCached ? ` ${t('chat.messages.cached')}=${message.tokensCached}` : ''}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ToolCallsAccordion({ calls }: { calls: ChatMessageToolCall[] }): JSX.Element {
  const { t } = useTranslation();
  const [expandedIdx, setExpandedIdx] = React.useState<number | null>(null);
  return (
    <div className="w-full space-y-1">
      {calls.map((c, i) => (
        <div key={`${c.callId}-${i}`} className="rounded-md border bg-muted/30">
          <button
            type="button"
            onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
            className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-accent/30"
          >
            <Wrench className="h-3 w-3 text-muted-foreground" />
            <code className="font-mono">{c.toolName}</code>
            {c.error ? (
              <Badge variant="destructive" className="text-[10px]">
                error
              </Badge>
            ) : null}
            {c.proposalId ? (
              <Badge variant="warning" className="text-[10px]">
                proposal
              </Badge>
            ) : null}
            <ChevronDown
              className={cn(
                'ml-auto h-3 w-3 transition-transform',
                expandedIdx === i && 'rotate-180',
              )}
            />
          </button>
          {expandedIdx === i ? (
            <div className="space-y-2 border-t p-2">
              <div>
                <div className="mb-1 text-[10px] uppercase text-muted-foreground">{t('chat.messages.args')}</div>
                <JsonView value={c.arguments} maxBytes={10_000} />
              </div>
              {c.error ? (
                <div>
                  <div className="mb-1 text-[10px] uppercase text-destructive">{t('chat.messages.errorLabel')}</div>
                  <pre className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-[11px] text-destructive">
                    {c.error}
                  </pre>
                </div>
              ) : c.result !== undefined ? (
                <div>
                  <div className="mb-1 text-[10px] uppercase text-muted-foreground">{t('chat.messages.result')}</div>
                  <JsonView value={c.result} maxBytes={10_000} />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
